import { Component, Input, OnInit, signal, inject } from '@angular/core';
import { ActivatedRoute, NavigationExtras, Params, QueryParamsHandling, Router } from '@angular/router';
import { PageEvent } from '@classi/spen';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';

import { pick, findKey, omit, isEqual, merge, pickBy, isNil } from 'lodash';
import { SpaceService } from '@components/space/space.service';
import { LayoutService } from '@components/layout/layout.service';
import { TeacherFilterService } from '@services/filter/teacher-filter.service';
import { SpaceAlbum } from './space-album';
import { checkBlankString } from '@functions/check-string';
import { SearchConditionSavingService } from '@services/search-condition-saving.service';

@Component({
  selector: 'app-space-albums',
  templateUrl: './space-albums.component.html',
  styleUrls: ['./space-albums.component.scss'],
  standalone: false,
})
export class SpaceAlbumsComponent implements OnInit {
  private formBuiler = inject(UntypedFormBuilder);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private spaceService = inject(SpaceService);
  layoutService = inject(LayoutService);
  filterService = inject(TeacherFilterService);
  private searchConditionSavingService = inject(SearchConditionSavingService);

  @Input() spaceMemberNumber: number;
  contentLoaded = false;
  form: UntypedFormGroup;

  iconSort = {
    none: 'sort-column none',
    loader: 'sort-loader fa fa-spinner fa-spin',
    up: 'sort-column fa fa-caret-up',
    down: 'sort-column fa fa-caret-down',
  };

  sortStatus = {
    created_at: this.iconSort.none,
  };

  inProgressSort = false;
  sortKey = null;
  sortOrder = null;
  queryParams = {};
  private readonly allowedParamKeys = ['page', 'search_key', 'distributor', 'sort_key', 'sort_order'];
  private readonly disallowedUrlParamKeys = ['search_key'];
  saveConditionKeys = ['distributor', 'sort_key', 'sort_order'];
  albums: SpaceAlbum[] = [];
  readonly page = signal({
    current_page: 1,
    next_page: null,
    per_page: 1,
    prev_page: null,
    total_count: 1,
    total_pages: 1,
  });

  sortMap = {
    '配信日時（降順）': { sort_key: 'created_at', sort_order: 'desc' },
    '配信日時（昇順）': { sort_key: 'created_at', sort_order: 'asc' },
  };

  distributorOptions = [
    { formValue: null, text: 'すべて' },
    { formValue: 'myself', text: '自分' },
    { formValue: 'other_teachers', text: '自分以外の先生' },
  ];

  isShowFilter = false;
  isSearching = false;
  messageSort: string;

  ngOnInit(): void {
    this.loadList(this.route.snapshot.queryParams);
  }

  private loadList(params: Params): void {
    const spaceId = +this.route.snapshot.paramMap.get('id');

    if (this.inProgressSort === false) {
      this.contentLoaded = false;
    }

    const url = this.router.url.split('?')[0];
    let newParams: Params;
    if (Object.keys(params).length === 1) {
      newParams = this.searchConditionSavingService.getSearchConditions(url, 'albums');
    } else {
      newParams = params;
      const saveConditions = pick(params, this.saveConditionKeys);
      if (!this.layoutService.isTabletDownView.value) {
        delete saveConditions['sort_key'];
        delete saveConditions['sort_order'];
      }
      this.searchConditionSavingService.saveSearchConditions(url, saveConditions, 'albums');
    }
    const page = Number(params['page']);
    const queryParams = pick(newParams, this.allowedParamKeys);
    queryParams['page'] = isNaN(page) || page === 0 ? 1 : page;
    this.queryParams = queryParams;
    this.filterService.setFilter(this.queryParams);
    this.sortOrder = this.queryParams['sort_order'];
    this.sortKey = this.queryParams['sort_key'];
    this.buildForm();
    this.loadSpaceListAlbum(spaceId, this.queryParams);
  }

  private navigate(params: Params, paramsHandling?: QueryParamsHandling): void {
    const apiParams = pickBy(params, (value) => !isNil(value));
    this.loadList(apiParams);

    const urlParams = omit(params, this.disallowedUrlParamKeys);
    const extras: NavigationExtras = { queryParams: urlParams, relativeTo: this.route, replaceUrl: true };
    if (paramsHandling !== undefined) {
      extras.queryParamsHandling = paramsHandling;
    }
    this.router.navigate(['.'], extras);
  }

  buildForm() {
    this.messageSort = this.getSortValue();
    this.form = this.formBuiler.group({
      search_key: [this.queryParams['search_key'] || '', Validators.maxLength(50)],
      distributor: [this.queryParams['distributor'] || null],
      sort_value: [this.messageSort],
    });
    this.isSearching = !isEqual(this.form.value, this.defaultFilterValues());
  }

  defaultFilterValues() {
    const filterValues = {
      distributor: null,
      search_key: '',
    };

    if (this.layoutService.isTabletDownView.value) {
      filterValues['sort_value'] = Object.keys(this.sortMap)[0];
    }

    return filterValues;
  }

  loadSpaceListAlbum(spaceId: number, params: {}) {
    this.spaceService.getTeacherAlbumsOfSpace(spaceId, params).subscribe(
      (response) => {
        this.page.set({ ...this.page(), ...response.meta });
        this.refreshIconSort();
        this.albums = response.albums;
        this.contentLoaded = true;
        this.isSearching = !this.isDefaultForm;
      },
      (error) => {
        // TODO: add error handling.
      },
    );
  }

  get isLoadingData(): boolean {
    return !this.contentLoaded || this.inProgressSort;
  }

  onClickAlbum(album: SpaceAlbum) {
    this.router.navigateByUrl(`albums/teacher/delivery/${album.id}`);
  }

  get showPagination(): boolean {
    return this.page().total_pages > 1;
  }

  pageChanged({ index }: PageEvent) {
    const params = merge(this.queryParams, { page: index + 1 });
    this.navigate(params, 'merge');
    window.scrollTo(0, 0);
  }

  changeBySortColumn(sortKey: string) {
    if (this.inProgressSort) {
      return false;
    }
    this.inProgressSort = true;

    if (sortKey === this.sortKey) {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortStatus[this.sortKey] = this.iconSort.none;
      this.sortOrder = 'desc';
      this.sortKey = sortKey;
    }
    this.sortStatus[this.sortKey] = this.iconSort.loader;

    const params = merge(this.queryParams, this.buildSortParams());
    this.navigate(params, 'merge');
  }

  buildSortParams() {
    return {
      page: 1,
      sort_key: this.sortKey,
      sort_order: this.sortOrder,
    };
  }

  refreshIconSort() {
    this.sortKey = this.sortKey || 'created_at';
    const icon = this.sortOrder === 'asc' ? 'up' : 'down';
    this.sortStatus[this.sortKey] = this.iconSort[icon];
    this.inProgressSort = false;
  }

  changeShowFilterStatus() {
    this.isShowFilter = !this.isShowFilter;
  }

  getSortValue() {
    const formValue = findKey(this.sortMap, pick(this.queryParams, ['sort_key', 'sort_order']));
    return formValue || Object.keys(this.sortMap)[0];
  }

  removeFilter() {
    if (this.layoutService.isTabletDownView.value) {
      this.isShowFilter = false;
      const sortDefault = Object.keys(this.sortMap)[0];
      this.queryParams['sort_value'] = sortDefault;
      Object.assign(this.queryParams, this.sortMap[sortDefault]);
    }

    this.form.reset(this.defaultFilterValues());
    this.onSubmitFilter();
  }

  onSubmitFilter() {
    window.scrollTo(0, 0);
    this.isShowFilter = false;
    this.navigate(this.buildFilterParams());
  }

  buildFilterParams() {
    const params = this.form.value;
    if (checkBlankString(params['search_key'])) {
      delete params['search_key'];
      this.form.controls['search_key'].setValue(null);
    }
    params['request_timestamp'] = Date.now();

    return Object.assign({ tab: 'albums' }, omit(params, ['sort_value']), this.sortMap[this.getSortValue()]);
  }

  onChangeSortValue(value: string) {
    this.queryParams['sort_value'] = value;
    Object.assign(this.queryParams, this.sortMap[value]);
  }

  get isDefaultForm(): boolean {
    const compareValues = this.form.value;

    if (!this.layoutService.isTabletDownView.value) {
      delete compareValues['sort_value'];
    }

    return isEqual(compareValues, this.defaultFilterValues());
  }

  get haveListItem(): boolean {
    return this.albums.length > 0;
  }
}
